import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import DirectoriesAddOnModal from '@features/DirectoriesAddOnModal';
import DirectoryLogoStrip from '@features/DirectoryLogoStrip';
import ExpandedSection from '@components/ExpandedSection';
import { ModalConfirm } from '@components/Modal';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import Tooltip from '@components/Tooltip';
import StatusChip, { STATUS_PALETTE } from '@components/StatusChip';
import { billingService, companyService } from '@services';
import { accountActions, alertActions, locationsProfilesActions } from '@actions';
import { BILLING_TIME_MAP, DIRECTORIES_ADDON_STATUS } from '@constants/billing';
import { LocationIntegrationStatus } from '@constants/companies';
import { LocationIntegrationName } from '@constants/integrations';
import { RoutePath } from '@constants/routes';
import { getExpiredAtInfo } from '@helpers/dates';
import { getPricingPlans } from '@helpers/billing';
import { isDirectoriesCountrySupported } from '@helpers/directories';
import { handleApiError } from '@helpers/api';
import { useTranslation, useLocationDirectoriesCount } from '@hooks';
import usePermissions from '@hooks/usePermissions';

const Promo = styled('div')({
    background:
        'linear-gradient(135deg, var(--secondary-light) 0%, var(--action-highlight) 55%, var(--tertiary-light) 100%)',
    border: '1px solid var(--secondary-main)',
    borderRadius: 8,
    padding: '18px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    '@media (max-width: 600px)': { flexDirection: 'column', alignItems: 'stretch' },
});

const StatusCard = styled('div')({
    border: '1px solid var(--divider)',
    borderRadius: 8,
    padding: '14px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    '@media (max-width: 600px)': { flexDirection: 'column', alignItems: 'stretch' },
});

const PriceBlock = styled('div')({
    flex: 'none',
    textAlign: 'right',
    '@media (max-width: 600px)': { textAlign: 'left' },
});

const caption = { display: 'block', color: 'var(--text-tertiary)' };

const LocalDirectories = ({ account, selectedCompany, setSelectedCompany, isInitialFormValid, isOpen, onToggle }) => {
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { hasFullSettings } = usePermissions();

    const [addOns, setAddOns] = useState(null);
    const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);

    const locationId = selectedCompany?.companyId;
    const viewListingsPath = `${RoutePath.LocationsProfiles}?locationId=${locationId}&type=directories`;

    const integration = selectedCompany?.integrations?.find(
        item => item.type === LocationIntegrationName.ListingDirectories,
    );
    const isPublished = integration?.status === LocationIntegrationStatus.Connected;
    const isPublishing = integration?.status === LocationIntegrationStatus.Pending;

    const listingsCount = useLocationDirectoriesCount(locationId, isPublished);
    const publishedLabel =
        listingsCount > 0
            ? t('SettingsCompanies.publishedDirectories', { count: listingsCount })
            : t('SettingsCompanies.published');

    const load = useCallback(() => {
        billingService
            .getDirectoriesAddOns()
            .then(setAddOns)
            .catch(() => setAddOns(null));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const enrollment = addOns?.enrollments?.find(item => item.locationId === locationId);
    const includedInPlan = addOns?.includedInPlan === true;
    const canManage = hasFullSettings();

    const pricing = getPricingPlans(account?.locale)[BILLING_TIME_MAP.MONTHLY];
    const { expiredAt: activatedAt } = getExpiredAtInfo(enrollment?.activatedAt, language);
    const { expiredAt: minimumTermEndsAt } = getExpiredAtInfo(enrollment?.minimumTermEndsAt, language);

    const onPublishFree = async () => {
        try {
            const updated = await companyService.connectListingDirectories(locationId);
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(locationId));
            setSelectedCompany(updated);
            dispatch(alertActions.success(t('SettingsCompanies.locationPublishedMsg')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onCancel = async () => {
        try {
            await billingService.cancelDirectoriesAddOn({ locationId });
            load();
            setCancelModalOpen(false);
            dispatch(alertActions.success(t('SettingsCompanies.directoriesCancelledMsg')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onDisconnectFree = async () => {
        try {
            const updated = await companyService.disconnectIntegration(locationId, integration.id);
            await dispatch(accountActions.get());
            setSelectedCompany(updated);
            await dispatch(locationsProfilesActions.get(locationId));
            setDisconnectModalOpen(false);
            dispatch(alertActions.success(t('SettingsCompanies.profileDisconnected')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onEnrolled = async updated => {
        setSelectedCompany(updated);
        await dispatch(accountActions.get());
        await dispatch(locationsProfilesActions.get(locationId));
        load();
    };

    if (!account?.allowListingDirectories) {
        return null;
    }

    if (!addOns || !addOns.allowed) {
        return null;
    }

    // Gates only new publishing/enrollment — existing enrollments stay manageable regardless.
    const countrySupported = isDirectoriesCountrySupported(selectedCompany?.country);

    let content;
    if (includedInPlan) {
        if (!isPublished && !isPublishing && !countrySupported) {
            return null;
        }
        content = (
            <StatusCard>
                <div>
                    <StatusChip
                        {...(isPublished
                            ? STATUS_PALETTE.success
                            : isPublishing
                              ? STATUS_PALETTE.info
                              : STATUS_PALETTE.neutral)}
                        label={
                            isPublished
                                ? publishedLabel
                                : isPublishing
                                  ? t('common.inProgress')
                                  : t('SettingsCompanies.notPublished')
                        }
                    />
                    <Typography variant="caption" sx={{ ...caption, marginTop: '6px' }}>
                        {t('SettingsCompanies.directoriesIncludedInPlan')}
                    </Typography>
                </div>
                {!isPublished && !isPublishing ? (
                    <Tooltip title={isInitialFormValid ? '' : t('validation.completeAllFields')} placement="top">
                        <span>
                            <Button variant="contained" onClick={onPublishFree} disabled={!isInitialFormValid}>
                                {t('buttons.publishProfile')}
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="text" onClick={() => navigate(viewListingsPath)}>
                            {t('SettingsCompanies.viewListings')}
                        </Button>
                        <Button variant="danger" onClick={() => setDisconnectModalOpen(true)}>
                            {t('buttons.disconnect')}
                        </Button>
                    </div>
                )}
            </StatusCard>
        );
    } else if (enrollment) {
        const isPendingCancellation = enrollment.status === DIRECTORIES_ADDON_STATUS.PENDING_CANCELLATION;
        const { expiredAt: entitlementEndsAt } = getExpiredAtInfo(enrollment.entitlementEndsAt, language);

        content = (
            <StatusCard>
                <div>
                    <StatusChip
                        {...(isPublished ? STATUS_PALETTE.success : STATUS_PALETTE.info)}
                        label={isPublished ? publishedLabel : t('common.inProgress')}
                    />
                    {(isPendingCancellation ? entitlementEndsAt : activatedAt && minimumTermEndsAt) && (
                        <Typography variant="caption" sx={{ ...caption, marginTop: '6px' }}>
                            {isPendingCancellation
                                ? t('SettingsCompanies.directoriesEndsOn', { date: entitlementEndsAt })
                                : t('SettingsCompanies.directoriesSinceCancellableAfter', {
                                      since: activatedAt,
                                      cancellableAfter: minimumTermEndsAt,
                                  })}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--primary-main)', cursor: 'pointer' }}
                        onClick={() => navigate(viewListingsPath)}
                    >
                        {t('SettingsCompanies.viewListings')}
                    </Typography>
                </div>
                {!isPendingCancellation && canManage ? (
                    <Tooltip
                        title={
                            enrollment.canCancel || !minimumTermEndsAt
                                ? ''
                                : t('SettingsCompanies.directoriesCancellableAfter', {
                                      date: minimumTermEndsAt,
                                  })
                        }
                        placement="top"
                    >
                        <span>
                            <Button
                                variant="danger"
                                onClick={() => setCancelModalOpen(true)}
                                disabled={!enrollment.canCancel}
                            >
                                {t('SettingsCompanies.cancelLocalDirectories')}
                            </Button>
                        </span>
                    </Tooltip>
                ) : null}
            </StatusCard>
        );
    } else {
        if (!countrySupported) {
            return null;
        }
        content = (
            <Promo>
                <div>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {t('SettingsCompanies.directoriesPromoTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', margin: '6px 0 12px' }}>
                        {t('SettingsCompanies.directoriesPromoDescription', {
                            location: selectedCompany?.internalName,
                        })}
                    </Typography>
                    <DirectoryLogoStrip />
                </div>
                <PriceBlock>
                    <Typography variant="h5" component="span">
                        {`${pricing.symbol}${pricing.directories}`}
                    </Typography>
                    <Typography variant="caption" component="span" sx={{ color: 'var(--text-secondary)' }}>
                        {` / ${t('SettingsGeneral.month')}`}
                    </Typography>
                    <Typography variant="caption" sx={{ ...caption, marginBottom: '8px' }}>
                        {t('SettingsCompanies.perLocation')}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setPurchaseModalOpen(true)}
                        disabled={!canManage || !locationId}
                    >
                        {t('SettingsCompanies.addLocalDirectories')}
                    </Button>
                </PriceBlock>
            </Promo>
        );
    }

    return (
        <>
            <ExpandedSection isOpen={isOpen} onClick={onToggle} text={t('SettingsCompanies.localDirectories')}>
                {content}
            </ExpandedSection>
            {purchaseModalOpen ? (
                <DirectoriesAddOnModal
                    isOpen={purchaseModalOpen}
                    location={{ id: locationId, name: selectedCompany?.internalName }}
                    onClose={() => setPurchaseModalOpen(false)}
                    onEnrolled={onEnrolled}
                />
            ) : null}
            {cancelModalOpen ? (
                <ModalConfirm
                    isOpen={cancelModalOpen}
                    handleClose={() => setCancelModalOpen(false)}
                    title={t('SettingsCompanies.cancelLocalDirectories')}
                    description={t('SettingsCompanies.cancelLocalDirectoriesDescription')}
                    onPrimaryAction={onCancel}
                    primaryActionText={t('buttons.confirm')}
                    primaryActionType="danger"
                />
            ) : null}
            {disconnectModalOpen ? (
                <ModalConfirm
                    isOpen={disconnectModalOpen}
                    handleClose={() => setDisconnectModalOpen(false)}
                    title={t('SettingsCompanies.disconnectIntegration')}
                    description={t('SettingsCompanies.disconnectIntegrationMessage')}
                    onPrimaryAction={onDisconnectFree}
                    primaryActionText={t('buttons.disconnect')}
                    primaryActionType="danger"
                />
            ) : null}
        </>
    );
};

export default LocalDirectories;
